import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

const LEAD_CLEARANCE_PX = 46
// An edge control moves by most of a screenful, not all of it, so the tab you
// were looking at stays partly visible for orientation.
const PAGE_SCROLL_RATIO = 0.7
// Sub-pixel layout means scrollLeft rarely hits its exact bounds; treat
// anything within this many pixels of an edge as being at that edge.
const EDGE_EPSILON_PX = 2
// Long enough for a real smooth scroll to have moved at least a pixel, short
// enough that the instant fallback doesn't read as a delayed jump.
const SMOOTH_SCROLL_PROBE_MS = 120

// Widths of the two edge zones. Shared by the styles below and by the
// focus-rescue clear-region maths, which would otherwise silently disagree
// with them.
export const LEFT_EDGE_WIDTH_PX = 98
export const RIGHT_EDGE_WIDTH_PX = 150

// Moves focus off a zone that is about to be hidden, so paging to either end
// with the keyboard doesn't strand the user on <body>. `sibling` is the
// opposite edge control, which is the natural place to land — after paging to
// the far right the "back" control is what you want next. When neither control
// is available (the row fits, so both hide) focus falls to the selected tab.
const rescueFocusBeforeHiding = (
  zone: HTMLDivElement | null,
  willHide: boolean,
  sibling: HTMLDivElement | null,
  siblingWillHide: boolean,
  scroller: HTMLDivElement
) => {
  if (!zone || !willHide || zone.hidden) return
  const active = document.activeElement
  if (!active || !zone.contains(active)) return

  // `siblingWillHide` matters as much as the sibling's current state: on a
  // resize that makes every tab fit, BOTH controls hide in this same pass.
  // Handing focus to a sibling that is about to disappear just moves the
  // problem — and, because the rescues run before either assignment, it used
  // to bounce focus from one doomed control to the other and then to <body>.
  const siblingButton =
    sibling && !sibling.hidden && !siblingWillHide
      ? sibling.querySelector<HTMLElement>('button')
      : null
  if (siblingButton) {
    siblingButton.focus()
    return
  }

  // No control will survive (the row now fits): land on the selected tab,
  // which is the only thing here a keyboard user would want to continue from.
  scroller
    .querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    ?.focus()
}

/**
 * The scrolling, measuring and focus behaviour behind `ScrollingTabNav`.
 *
 * Split out from the component because that component was doing four jobs at
 * once — rendering, observing, scrolling and focus management — and the
 * interesting bugs all lived in the last three. Keeping them here lets them be
 * read and tested without a tab row in the way, the same split
 * `InfiniteScrollContainer` makes with `useInfiniteScrollWithTrigger`.
 *
 * Returns the refs the component must attach, the count of tabs currently off
 * the right edge, and the handlers it wires up.
 */
export const useScrollingTabNav = () => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const leftEdgeRef = useRef<HTMLDivElement>(null)
  const rightEdgeRef = useRef<HTMLDivElement>(null)
  // The tab row itself — the scroll content, whose width is what overflows.
  const navRef = useRef<HTMLDivElement>(null)
  // The pending smooth-scroll probe (below). Tracked so a later page
  // supersedes an earlier one rather than both firing, and so nothing is left
  // queued after unmount.
  const probeRef = useRef<number | null>(null)

  // Where the last paging request was headed. Paging measures from this rather
  // than from the live `scrollLeft`, which is a partial position while a smooth
  // scroll is still animating — reading it mid-flight makes each rapid click
  // advance less than a full page.
  const pagedToRef = useRef<number | null>(null)
  // Set for as long as a scroll WE started is still settling. Our own animation
  // emits a scroll event per frame, every one of them far from the target, so
  // without this the handler below treats the first frame as the viewer taking
  // over and clears the baseline immediately — which left the ref doing nothing
  // in exactly the case it exists for.
  const selfScrollingRef = useRef(false)

  // The count is React state so it lands in the button's accessible name; a
  // screen reader gets nothing from a textContent mutation React doesn't know
  // about. setState bails out when the value is unchanged, so this re-renders
  // only when a tab actually crosses the edge, not on every scroll frame.
  const [hiddenCount, setHiddenCount] = useState(0)

  // Edge visibility stays a direct DOM write: it flips on scroll frames, and
  // re-rendering the whole row that often is wasteful. `hidden` is the right
  // tool anyway — it removes the zone from the accessibility tree.
  const sync = useCallback(() => {
    const scroller = scrollRef.current
    if (!scroller) return

    const maxScroll = scroller.scrollWidth - scroller.clientWidth
    const atStart = scroller.scrollLeft <= EDGE_EPSILON_PX
    const atEnd = scroller.scrollLeft >= maxScroll - EDGE_EPSILON_PX

    // Hiding a zone that currently holds focus would drop the caret to
    // <body>: `hidden` is display:none, so the focused button stops being
    // rendered and the browser blurs it. That is exactly what happens on the
    // last press of a paging control — you page to the end, the control that
    // took you there disappears, and a keyboard user loses their place with
    // nowhere sensible to resume from. Move focus to the other control when it
    // is still available, else to the selected tab, before hiding.
    rescueFocusBeforeHiding(
      leftEdgeRef.current,
      atStart,
      rightEdgeRef.current,
      atEnd,
      scroller
    )
    rescueFocusBeforeHiding(
      rightEdgeRef.current,
      atEnd,
      leftEdgeRef.current,
      atStart,
      scroller
    )

    if (leftEdgeRef.current) {
      leftEdgeRef.current.hidden = atStart
    }
    if (rightEdgeRef.current) {
      rightEdgeRef.current.hidden = atEnd
    }

    // Compare against the scroller's own right edge, so a tab peeking out
    // from under "+N more" still counts as visible — matching the design,
    // where the peek is the affordance rather than a hidden tab.
    const visibleRight = scroller.getBoundingClientRect().right
    const hidden = Array.from(
      scroller.querySelectorAll<HTMLElement>('[role="tab"]')
    ).filter(tab => tab.getBoundingClientRect().right > visibleRight + 1)
    setHiddenCount(hidden.length)
  }, [])

  // Bring the active tab into view whenever the selection arrives from
  // somewhere other than a click on the row itself:
  //   - on mount, so a deep link (#item_popularity_in_digital_orders) opens
  //     with its tab on screen rather than scrolled off the right edge
  //   - when the hash changes and the host page follows it, which can select
  //     a tab that is currently scrolled out of sight
  // Clicks are already handled by click-to-lead, and land on the same
  // position, so this is a no-op for them.
  //
  // Keyed on which tab was last led, not a once-per-mount flag: the viewer's
  // own scrolling is left alone as long as the selection hasn't moved.
  const lastLedTab = useRef<string | null>(null)

  useLayoutEffect(() => {
    sync()

    const scroller = scrollRef.current
    if (!scroller) return

    // Layout may not have settled yet (web fonts in particular). Returning
    // without recording anything means this retries on the next render rather
    // than pinning the row at 0.
    const maxScroll = scroller.scrollWidth - scroller.clientWidth
    if (maxScroll <= 0) return

    const active = scroller.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]'
    )
    if (!active) return

    const activeKey =
      active.getAttribute('data-rr-ui-event-key') ?? active.textContent ?? ''
    if (lastLedTab.current === activeKey) return
    lastLedTab.current = activeKey

    const offsetWithinContent =
      active.getBoundingClientRect().left -
      scroller.getBoundingClientRect().left +
      scroller.scrollLeft

    // A repositioning, not a response to an interaction, so suppress the CSS
    // smooth-scroll for this one assignment — otherwise the row visibly
    // glides into place on every mount.
    const previousBehavior = scroller.style.scrollBehavior
    scroller.style.scrollBehavior = 'auto'
    scroller.scrollLeft = Math.max(
      0,
      Math.min(offsetWithinContent - LEAD_CLEARANCE_PX, maxScroll)
    )
    scroller.style.scrollBehavior = previousBehavior

    sync()
  })

  useEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return

    // A scroll we did not initiate means the viewer took over; the paging
    // baseline has to follow them rather than our last target.
    const onScroll = () => {
      // Only a scroll the viewer drove should move the baseline. While one of
      // ours is settling every frame reports a partial position, so treating
      // those as viewer input would discard the target we are heading for.
      if (selfScrollingRef.current) {
        if (
          pagedToRef.current !== null &&
          Math.abs(scroller.scrollLeft - pagedToRef.current) <= EDGE_EPSILON_PX
        ) {
          // Arrived: subsequent events are the viewer's again.
          selfScrollingRef.current = false
        }
      } else {
        pagedToRef.current = null
      }
      sync()
    }
    scroller.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', sync)

    // Catches container width changes that aren't window resizes (the nav
    // collapsing, a sidebar opening) and tabs appearing once a flag or
    // permission resolves.
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(sync) : null
    observer?.observe(scroller)
    // The scroller's own box is sized by the parent container, so it does not
    // change when the tabs inside it get wider — and a scrollWidth change
    // fires no scroll or resize event either. Observing the row itself is what
    // catches that: most visibly a web font swapping in after first paint,
    // which widens every label. Without it the row can end up overflowing with
    // no "+N more" and no back control, because the only measurement happened
    // while the fallback font made everything fit.
    if (navRef.current) observer?.observe(navRef.current)

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', sync)
      observer?.disconnect()
      // A queued smooth-scroll probe must not outlive the component. It
      // null-guards the ref so a late fire was harmless, but leaving a timer
      // pending after unmount keeps tests from ever observing that branch.
      if (probeRef.current !== null) {
        window.clearTimeout(probeRef.current)
        probeRef.current = null
      }
    }
  }, [sync])

  // Smooth scrolling cannot be relied on: in some environments it is simply
  // inert. Chrome under automation ignores both scrollTo({behavior:'smooth'})
  // and a scrollLeft assignment under CSS scroll-behavior:smooth, while an
  // explicitly instant scroll on the very same element works — so a control
  // wired only to the smooth path silently does nothing at all.
  //
  // So: ask for the (CSS-driven) smooth scroll, then check whether it
  // actually started and land it instantly if not. Smooth where it works,
  // still functional where it doesn't.
  const scrollToLeft = (left: number) => {
    const scroller = scrollRef.current
    if (!scroller) return

    const maxScroll = scroller.scrollWidth - scroller.clientWidth
    const target = Math.max(0, Math.min(left, maxScroll))
    const start = scroller.scrollLeft
    if (target === start) return

    selfScrollingRef.current = true
    scroller.scrollLeft = target
    // Don't wait for a scroll event to refresh the edge controls: a
    // programmatic scrollLeft change doesn't reliably emit one (it emits
    // nothing at all in Chrome under automation), which would leave "+N more"
    // and the "‹" showing stale state after our own scrolls. The listener is
    // still what covers wheel/trackpad/drag scrolling by the viewer.
    sync()

    if (probeRef.current !== null) window.clearTimeout(probeRef.current)
    probeRef.current = window.setTimeout(() => {
      probeRef.current = null
      const el = scrollRef.current
      // Unmoved means the animation never began. If the viewer scrolled by
      // hand in the meantime, scrollLeft will have changed and we leave it be.
      if (!el || el.scrollLeft !== start) {
        sync()
        return
      }

      const previousBehavior = el.style.scrollBehavior
      el.style.scrollBehavior = 'auto'
      el.scrollLeft = target
      el.style.scrollBehavior = previousBehavior
      sync()
    }, SMOOTH_SCROLL_PROBE_MS)
  }

  // Step one "page" in either direction. Both edge controls go through this,
  // so "+N more" advances by exactly as much as "‹" goes back.
  //
  // Note this deviates from the prototype, where "+N more" jumped straight to
  // the far end (scrollTo({left: scrollWidth})). That reads fine at six tabs,
  // where the end is about a screenful away, but at eighteen it vaults past a
  // dozen tabs in one go and loses the viewer's place.
  const scrollByPage = (direction: 1 | -1) => {
    const scroller = scrollRef.current
    if (!scroller) return

    const from = pagedToRef.current ?? scroller.scrollLeft
    const target =
      from + direction * Math.round(scroller.clientWidth * PAGE_SCROLL_RATIO)
    pagedToRef.current = Math.max(
      0,
      Math.min(target, scroller.scrollWidth - scroller.clientWidth)
    )
    scrollToLeft(target)
  }

  // Bring a tab to the leading position so the tabs after it come into view.
  const leadTab = (tab: HTMLElement) => {
    const scroller = scrollRef.current
    if (!scroller) return

    const offsetWithinContent =
      tab.getBoundingClientRect().left -
      scroller.getBoundingClientRect().left +
      scroller.scrollLeft

    scrollToLeft(offsetWithinContent - LEAD_CLEARANCE_PX)
  }

  const tabFromEvent = (target: EventTarget | null) =>
    (target as HTMLElement | null)?.closest<HTMLElement>('[role="tab"]') ?? null

  // Click-to-lead. Delegated from the scroller so TabItem stays untouched and
  // this works for whatever it renders.
  const handleClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    const tab = tabFromEvent(event.target)
    if (tab) leadTab(tab)
  }

  // Arrow keys move selection through the tablist and call focus() on the new
  // tab, which makes the browser do its own minimal scroll-into-view — that
  // aligns the tab flush with the scroller edge, i.e. directly underneath an
  // edge zone's opaque backing, hiding the very tab just selected. Leading it
  // properly puts it clear of the overlay.
  const handleFocusCapture = (event: React.FocusEvent<HTMLDivElement>) => {
    const tab = tabFromEvent(event.target)
    if (!tab) return

    // Only rescue a tab that is actually obscured. Leading unconditionally
    // pinned the focused tab to the left edge on every arrow-key press, so a
    // keyboard user could never see the tabs they had already passed — the
    // opposite of what a mouse user sees. The case this exists for is the
    // browser's own minimal scroll-into-view parking a tab flush against the
    // edge, underneath an edge zone's opaque backing.
    const scroller = scrollRef.current
    if (!scroller) return

    const view = scroller.getBoundingClientRect()
    const rect = tab.getBoundingClientRect()
    const leftClear =
      view.left + (leftEdgeRef.current?.hidden ? 0 : LEFT_EDGE_WIDTH_PX)
    const rightClear =
      view.right - (rightEdgeRef.current?.hidden ? 0 : RIGHT_EDGE_WIDTH_PX)
    if (rect.left >= leftClear && rect.right <= rightClear) return

    leadTab(tab)
  }

  return {
    /** The horizontally scrolling viewport. */
    scrollRef,
    /** The tab row inside it — the scroll content, whose width overflows. */
    navRef,
    /** The two absolutely-positioned edge zones. */
    leftEdgeRef,
    rightEdgeRef,
    /** How many tabs currently sit past the right edge. */
    hiddenCount,
    /** Page the row by most of a screenful; 1 forward, -1 back. */
    scrollByPage,
    /** Bring a clicked tab to the leading position. */
    handleClickCapture,
    /** Rescue a focused tab from underneath an edge control. */
    handleFocusCapture,
  }
}

export default useScrollingTabNav
